/**
 * Billing workbench queues.
 *
 * Pure classification over booking_balances rows (the computed ledger view) so the page, the
 * tests and any report agree on what "overdue" or "stale draft" means. No I/O.
 *
 * Every row here is already active and not a placeholder unless the caller says otherwise; rows
 * flagged needs_backfill (paid in Stripe, unrecorded here) are never a debt.
 */

export const QUEUES = [
  { key: 'overdue', title: 'Overdue', hint: 'Balance past its due date, event still ahead' },
  { key: 'due_soon', title: 'Due in 7 days', hint: 'Balance due within a week' },
  { key: 'card_failing', title: 'Card failing', hint: 'Subscription past due' },
  { key: 'plan_stopped', title: 'Plan stopped', hint: 'Subscription canceled or unpaid, money still owed' },
  { key: 'drafts_to_finalize', title: 'Drafts to finalize', hint: 'Draft invoice past due, never finalized' },
  { key: 'missing_plan', title: 'Missing a plan', hint: 'Owes money, no invoice or subscription' },
  { key: 'stale_drafts', title: 'Stale drafts', hint: 'Booking paid, draft still open in Stripe' },
  { key: 'past_events', title: 'Past events with a balance', hint: 'Event over, money still open' },
  { key: 'overpaid', title: 'Overpaid', hint: 'Paid more than agreed: record what it bought, or refund it' }
]

const dayMs = 86400000
const dateStr = v => (v ? String(v).slice(0, 10) : '')
const addDays = (isoDate, n) => new Date(Date.parse(isoDate + 'T12:00:00Z') + n * dayMs).toISOString().slice(0, 10)

/**
 * @param {Array<object>} rows  booking_balances rows
 * @param {string} today        YYYY-MM-DD in the business timezone
 * @returns {{ byQueue: Record<string, object[]>, rowsWithQueues: Array<object> }}
 */
export function classifyBillingQueues(rows, today) {
  const weekOut = addDays(today, 7)
  const byQueue = Object.fromEntries(QUEUES.map(q => [q.key, []]))
  const rowsWithQueues = []

  for (const r of rows || []) {
    if (!r || r.is_active === false || r.is_placeholder) continue
    const remaining = Number(r.remaining_cents || 0)
    const owes = remaining > 0 && !r.needs_backfill
    const eventDate = dateStr(r.event_date)
    const due = dateStr(r.final_due_date)
    const eventPast = eventDate && eventDate < today
    const hasDraft = !!r.stripe_final_invoice_id && !r.final_invoice_finalized
    const monthly = r.payment_plan === 'monthly99' || r.payment_plan === 'monthly199'
    const planBased = monthly || r.payment_plan === 'deposit500'
    const queues = []

    if (hasDraft && remaining === 0 && !r.needs_backfill) queues.push('stale_drafts')
    // Paid above the agreed price and nothing owed: an add-on bought outside the wizard that nobody recorded, or
    // money to refund. Its own card since 21 Sep 2026 (34 bookings, $7,037, found one at a time until then).
    if (Number(r.overpaid_cents || 0) > 0 && remaining === 0 && !r.needs_backfill) queues.push('overpaid')
    if (owes && eventPast) queues.push('past_events')
    if (owes && !eventPast) {
      if (due && due < today) queues.push('overdue')
      else if (due && due >= today && due <= weekOut) queues.push('due_soon')
      if (r.subscription_status === 'past_due') queues.push('card_failing')
      // Stripe gave up on the card (canceled / unpaid) or the plan never got going (incomplete): the
      // monthly plan is dead and the money is still owed. Its own card since 18 Sep 2026, so it stops
      // hiding among deposit bookings that only need their balance draft.
      const planStopped = monthly && ['canceled', 'unpaid', 'incomplete', 'incomplete_expired'].includes(r.subscription_status)
      if (planStopped) queues.push('plan_stopped')
      if (hasDraft && due && due < today) queues.push('drafts_to_finalize')
      if (planBased && !planStopped && !r.stripe_final_invoice_id && !(monthly && r.stripe_subscription_id && ['active', 'trialing'].includes(r.subscription_status))) queues.push('missing_plan')
    }

    for (const q of queues) byQueue[q].push(r)
    rowsWithQueues.push({ ...r, queues })
  }

  for (const key of Object.keys(byQueue)) {
    byQueue[key].sort((a, b) => (dateStr(a.final_due_date) || dateStr(a.event_date)).localeCompare(dateStr(b.final_due_date) || dateStr(b.event_date)) || dateStr(a.event_date).localeCompare(dateStr(b.event_date)))
  }
  return { byQueue, rowsWithQueues }
}

/** Sum of remaining_cents for a queue; the overpaid queue sums overpaid_cents instead (nothing is owed there). */
export function queueTotalCents(rows, queueKey = null) {
  const field = queueKey === 'overpaid' ? 'overpaid_cents' : 'remaining_cents'
  return (rows || []).reduce((s, r) => s + Number(r[field] || 0), 0)
}

/** What the payment monitor would do, in words, for one row. Null when it would not touch the booking. */
export function monitorExposure(r, today) {
  if (!r || r.needs_backfill || Number(r.remaining_cents || 0) <= 0) return null
  if (!['deposit500', 'monthly199'].includes(r.payment_plan) || !r.due_date_set) return null
  const due = dateStr(r.final_due_date)
  if (!due || due >= today) return null
  const eventDate = dateStr(r.event_date)
  if (!eventDate || eventDate < today) return null
  const cancelFrom = addDays(eventDate, -30)
  return cancelFrom <= today
    ? { kind: 'cancel', from: cancelFrom, text: 'monitor would cancel now' }
    : { kind: 'late_fee', from: cancelFrom, text: `late fees now, cancel from ${cancelFrom}` }
}
